/**
 * **诊断码 → 人话**的唯一映射与渲染（2026-10-06 · task-26 D）。
 *
 * 背景（用户截图批注）：概览页与「整店历史 / 按店铺」表把 `STORAGE_*` 这类**内部诊断码**
 * 原样甩给用户（`STORAGE_GOOGLE_SPEND_CURRENCY_MISMATCH`、`SPEND_ALLOCATION_UNVERIFIED`…），
 * 既看不懂也分不清「要处理」还是「只是披露」。
 *
 * 三条纪律：
 *  1. **只做展示层翻译**：后端返回的码**一字不改**（那是机器接口，别的消费者按码判断）；
 *  2. **原码不丢**：收在 `<details>` 折叠区（排障时展开即可复制），正文只给人话；
 *  3. **未知码不得吞**：`known=false` → 正文写「未收录的诊断码」并直接展示后端原文 ——
 *     「未知 ≠ 无事发生」。
 *
 * 为什么单独成文件：概览页（`OverviewPage`）、按店铺表（`ShopMetricsTable`）、
 * 整店历史表（`ShopHistoryTable`）**三处共用**同一份映射；散在某个组件里导出会让
 * 「表格组件」变成奇特的依赖源。
 */

import type { CSSProperties, ReactElement } from "react";

/**
 * **已收录的诊断码 → 人话**。
 *
 * 码名**逐字**取自后端权威来源（见每条上方的 `源：` 注释）。翻译只讲两件事：
 * **发生了什么** + **对页面上的数字有什么影响**。
 *
 * ⚠️ 2026-10-06 教训：这里曾把 `STORAGE_SOURCE_FETCHED_AT_COLUMN_MISSING` /
 * `STORAGE_TIMEZONE_ALLOCATION_UNAVAILABLE` 写成**漏了 `STORAGE_` 前缀**的错码
 * （映射永远命不中 ⇒ 静默降级成「未收录」）。**改这里的码名必须先回权威来源逐字核对**：
 *  · 所有 `STORAGE_*`：`@ams/store` 的 `STORE_WARNING_CODES`（`packages/store/src/read-helpers.ts`）；
 *  · API 路由层自有码：`grep 'code: "' apps/api/src/app.ts`。
 */
const DIAGNOSTIC_MESSAGES: Readonly<Record<string, string>> = {
  // —— 币种 / 折算 ——
  // 源：packages/store/src/reader.ts:363
  STORAGE_GOOGLE_SPEND_CURRENCY_MISMATCH:
    "该店铺有 Google 账户，但它的结算币与本次显示的币种不一致，所以这次没有把 Google 花费计入分母：花费合计与全渠道比率显示「—」（绝不按 1:1 硬折）。",
  // 源：packages/store/src/read-helpers.ts:44（由 usdConversionWarnings 发出）
  STORAGE_FX_CONVERSION_INCOMPLETE:
    "有行因为当期没有可用汇率而**未计入**本次折算合计（这些行不按 0 相加，所以合计是下界）：补齐汇率并跑一次 fx-reconvert 后再复核。",
  // 源：apps/api/src/app.ts:1102
  FX_USD_CONVERSION_APPLIED:
    "金额已按 ECB 历史汇率折算为 USD；缺汇率的行不计入合计并单独告警（绝不按 1:1 折算）。",
  // 源：packages/store/src/read-helpers.ts:36（entity-metrics-reader.ts:212 发出）
  STORAGE_ENTITY_FACT_CURRENCY_MISMATCH:
    "有事实行的币种与该账户的币种不一致，已**丢弃**而不是相加（跨币种求和是本项目明令禁止的口径错误）。",

  // —— 分摊 / 时区 ——
  // 源：apps/api/src/app.ts:1117
  SPEND_ALLOCATION_UNVERIFIED:
    "该店铺的花费按「账户时区 → 店铺业务日」分摊的权重未经验证，店铺级花费是近似值（总额仍逐分守恒）。",
  // 源：packages/store/src/read-helpers.ts:66（shop-history-reader.ts:327 / metrics-allocation.ts:204 / spend-overview-reader.ts:300）
  STORAGE_SPEND_ALLOCATED_BY_OVERLAP_MINUTES:
    "广告花费已按「账户时区 → 店铺业务日」的重叠分钟分摊到店铺日；单平台自身的花费口径不变。",
  // 源：packages/store/src/reader.ts:649
  STORAGE_FACTS_REALLOCATED_TO_SHOP_DAY:
    "广告花费已从「账户业务日」归到「店铺业务日」再与销售额对比；单平台自身展示口径不变。",
  // 源：packages/store/src/read-helpers.ts:71（shop-history-reader.ts:265/315/335、metrics-allocation.ts:175/213、spend-overview-reader.ts:308/317）
  STORAGE_TIMEZONE_ALLOCATION_UNAVAILABLE:
    "跨时区分摊不可用（缺时区列或时区名非法）：这些行按账户日原样保留，跨平台按日对比的窗口错位风险未消除。",
  // 源：packages/store/src/read-helpers.ts:31（reader.ts:657 发出）
  STORAGE_TIMEZONE_CALIBRATION_UNAVAILABLE:
    "没有可用的时区校准结果：跨平台按日对比可能错位，请先跑一次时区校准。",

  // —— 覆盖 / 缺失（一律「—」而不是 0）——
  // 源：packages/store/src/read-helpers.ts:16（reader.ts:436、shop-history-reader.ts:368 发出）
  STORAGE_SHOPIFY_REVENUE_COVERAGE_GAP:
    "Shopify 真实收入在部分业务日缺行：这段窗口的真实收入显示「—」（只对已有行求和是下界，不等于 0）。",
  // 源：apps/api/src/assemble.ts:72
  SHOPIFY_CALIBER_UNAVAILABLE:
    "这段窗口没有可用的 Shopify 真实收入口径：真实收入与相关比率一律显示「—」。",
  // 源：packages/store/src/shop-history-reader.ts:376
  STORAGE_SHOP_HISTORY_NO_META_ACCOUNT:
    "这个范围里没有任何 Meta 实体归属：Meta 花费 / Meta ROAS / 对账 ROAS 显示「—」，而不是算成 0。",
  // 源：packages/store/src/shop-history-reader.ts:384
  STORAGE_SHOP_HISTORY_NO_GOOGLE_ACCOUNT:
    "这个范围里没有 Google Ads 账户：Google 花费 / GG ROAS / 对账 ROAS 显示「—」（分母不完整，绝不回退成只算 Meta）。",
  // 源：packages/store/src/reader.ts:378
  STORAGE_GOOGLE_SPEND_UNAVAILABLE:
    "Google 花费读不到（未接入 / 表不可读）：Google 花费与全渠道分母显示「—」，绝不回退成只算 Meta。",
  // 源：packages/store/src/read-helpers.ts:18（reader.ts:457 发出）
  STORAGE_SOURCE_FETCHED_AT_COLUMN_MISSING:
    "无法计算数据新鲜度（缺 `source_fetched_at` 列，或区间内没有事实行）。",

  // —— 归属质量 ——
  // 源：packages/store/src/read-helpers.ts:50（reader.ts:388 发出）
  STORAGE_GOOGLE_ATTRIBUTION_UNMATCHED:
    "有 Google 花费没有命中「系列名市场标记」规则，已按账户默认店铺归属：这些花费的站点归属未经规则确认，请核对系列命名。",

  // —— 概览端点自有码 ——
  // 源：apps/api/src/app.ts:3178
  STORAGE_SHOP_METRICS_ROW_UNAVAILABLE: "该店铺的整店口径读取失败：这一行显示「—」，不是 0。",
  // 源：apps/api/src/app.ts:3208
  STORAGE_SHOP_METRICS_PARTIAL: "有店铺读取失败：失败的行显示「—」，不是 0。",
  // 源：apps/api/src/app.ts:1788
  STORAGE_GOOGLE_METRICS_NO_ROWS:
    "这个窗口没有任何 Google 事实行：Google 花费/转化价值为 0、ROAS 显示「—」（不是 0）。",
  // 源：apps/api/src/app.ts:1548
  REVENUE_PENDING_BACKFILL:
    "区间内有广告花费但订单数为 0：若该站尚未回补订单，这是「数据未到」而不是「真实零收入」。",
  // 源：apps/api/src/app.ts:1559
  RECONCILE_WITHOUT_GOOGLE:
    "区间内没有 Google 花费数据，对账分母只含 Meta，对账 ROAS 会偏大，勿据此判断渠道效率。",

  // ---- 页面级 meta.warnings（2026-10-06 · task-33：7 个页面同一批收口）----

  // 源：apps/api/src/app.ts:1387（utmCoverageWarning，/v1/metrics/entities；AdsPage）
  UTM_REFERENCE_CALIBER:
    "UTM 参考口径的覆盖率说明：utmRoas 只覆盖按 UTM 命中的那部分订单，**不能当作全量收入口径**。",
  // 源：apps/api/src/params.ts:321（/v1/ad-tree、/v1/metrics/entities、/v1/google-ads/campaigns 的 limit 钳制）
  LIMIT_CLAMPED: "请求的行数超过服务端上限，已按上限截断：本页**不是全量**，请勿据此下结论。",
  // 源：packages/store/src/ad-tree-assemble.ts:72（/v1/ad-tree；AdManagementPage）
  STORAGE_AD_TREE_MISSING_FACTS:
    "有实体在事实表里没有对应行：这些节点的花费/转化按 0 显示，但**不等于「真的没花钱」**——等回填后再看。",
  // 源：packages/store/src/ad-tree-assemble.ts:74
  STORAGE_AD_TREE_CURRENCY_MISMATCH:
    "有事实行的币种与账户结算币不一致：这些节点按 0 处理（跨币种绝不相加）。",
  // 源：packages/store/src/ad-tree-assemble.ts:70（历史常量，分页后不再构树，保留兼容）
  STORAGE_AD_TREE_ORPHAN_NODES:
    "有节点的父级不在本次结果里（分页所致）：这些节点不参与构树（历史口径）。",
  // 源：apps/api/src/app.ts:2634（/v1/style-codes；StyleCodeBoardPage）
  CREATIVE_BOARD_SHOP_NOT_FOUND:
    "该店铺不在店铺表里：本次返回空看板（不猜店铺、不编数据），请核对站点选择。",
  // 源：apps/api/src/app.ts:2642
  CREATIVE_BOARD_SHOP_TIMEZONE_MISSING:
    "该店铺没配 `iana_timezone`：本次按 UTC 算业务日（窗口仍不含今天）——请补齐店铺时区，口径不会静默改变。",
  // 源：apps/api/src/app.ts:2650
  CREATIVE_BOARD_SHOP_TIMEZONE_INVALID:
    "该店铺的时区无法解析：本次按 UTC 算业务日；请修正 `shops.iana_timezone`。",
  // 源：apps/api/src/app.ts:2930（/v1/creatives；CreativeBoardPage）
  CREATIVE_BOARD_TRUNCATED:
    "当前视图超过行数上限，已截断：本页**不是全量**（可用游标继续翻页），不要用它下结论。",
  // 源：apps/api/src/app.ts:2940
  CREATIVE_BOARD_AD_NAME_MISSING:
    "有广告的**广告名为空**：素材名取广告名，所以这些广告不出现在看板里（也不计入覆盖率分母）——请先补齐目录同步的 `meta_entities.name`。",
  // 源：apps/api/src/app.ts:2951
  CREATIVE_BOARD_FX_INCOMPLETE:
    "累计窗口内有事实行因缺汇率**没有进入合计**：相关格子显示「—」（绝不按 0 相加、绝不 1:1）。",
  // 源：apps/api/src/app.ts:3040（/v1/creatives/corrected，抽屉打开时）
  CREATIVE_BOARD_CORRECTED_ESTIMATE:
    "「修正 ROAS」是按**账户当日 ROAS 折算的估计值**，不是 Meta 事实口径；未修正的裸累计 ROAS 另有列可对照。",
  // 源：apps/api/src/app.ts:1890（/v1/google-ads/campaigns；GoogleCampaignsPage）
  GOOGLE_ATTRIBUTION_UNMATCHED:
    "本页有系列的站点归属未定（未命中任何系列级市场规则）：站点维度不计入它们，列表照常展示并标记，请核对系列命名。",
  // 源：apps/api/src/app.ts:2287（/v1/timezones；SyncPage）
  TIMEZONE_NEEDS_ALLOCATION:
    "有店铺的广告来源与店铺业务日不等效：跨平台按日对比必须按重叠分钟分摊（R18），否则窗口会错位。",
  // 源：apps/api/src/app.ts:2315（/v1/sync/status；SyncPage / SyncStatusPage）
  UNKNOWN_SHOP:
    "请求的店铺 id 在店铺表里不存在：返回空列表——**空白 = 店铺未知**，不是「没有同步记录」。",
  // 源：apps/api/src/app.ts:2325（/v1/sync/status）
  SYNC_LEDGER_NOT_TRACKED:
    "该平台尚未纳入运行台账：页面显示的「没有记录」**不代表它从没跑过**，只是台账里看不到。",

  // —— 凭证健康（概览页顶部横幅；2026-10-06 诊断码收尾批次）——
  // 源：apps/api/src/app.ts:1695（`GET /v1/credentials` 的 meta.warnings；CredentialBanner 文件头同样点名这两个码）
  CREDENTIAL_EXPIRED:
    "有数据源的访问令牌**已过期**：该平台的同步会失败，请更换令牌后重启 worker。",
  CREDENTIAL_EXPIRING:
    "有数据源的访问令牌**即将过期**：请尽快更换，避免同步中断（剩余天数见横幅明细）。",
};

/** 一条诊断的展示层描述。 */
export interface DiagnosticDescription {
  readonly code: string;
  /** 中文人话（已收录）；未知码给「未收录」提示（不吞、不编）。 */
  readonly text: string;
  readonly known: boolean;
  /** 后端原文（始终保留，未知码直接展示）。 */
  readonly rawMessage: string;
}

/**
 * 诊断码 → 展示层描述（纯函数，未知码**不吞**）。
 *
 * 未知码：`known=false`，`text` 明确写「未收录的诊断码」，并且调用方必须把
 * `rawMessage`（后端原文）与 `code` 一并展示 —— 「未知 ≠ 无事发生」。
 */
export function describeDiagnostic(code: string, message: string): DiagnosticDescription {
  const text = DIAGNOSTIC_MESSAGES[code];
  if (text !== undefined) return { code, text, known: true, rawMessage: message };
  return {
    code,
    text: "未收录的诊断码（本页不隐藏后端原文，请连同下面的码与原文反馈给工程）",
    known: false,
    rawMessage: message,
  };
}

export interface DiagnosticWarningsProps {
  readonly warnings: readonly { readonly code: string; readonly message: string }[];
  /** 外层容器类名（概览页与各表格的底色/边框不同，由调用方给）。 */
  readonly className: string;
  /** 可选内联样式（个别页面历史上给容器加了 `borderRadius`，保持视觉不变）。 */
  readonly style?: CSSProperties;
}

/**
 * 诊断告警列表：**人话在前，原码折叠在后**。
 *
 * 原码不裸暴露给用户，但**不丢**：收在 `<details>` 折叠区里（排障时展开即可复制），
 * 未知码则把人话位置写成「未收录」并把后端原文直接显示。
 */
export function DiagnosticWarnings({ warnings, className, style }: DiagnosticWarningsProps): ReactElement | null {
  if (warnings.length === 0) return null;
  return (
    <ul className={className} {...(style === undefined ? {} : { style })}>
      {warnings.map((warning) => {
        const described = describeDiagnostic(warning.code, warning.message);
        return (
          <li key={`${warning.code}:${warning.message}`}>
            <span>{described.text}</span>
            {described.known ? null : (
              <span className="ml-1 font-mono">{described.rawMessage}</span>
            )}
            <details className="mt-0.5">
              <summary className="cursor-pointer text-2xs opacity-70">
                诊断码（排障用）{described.known ? "" : " · 未收录"}
              </summary>
              <span className="font-mono text-2xs">
                {described.code}
                {described.known ? ` · ${described.rawMessage}` : null}
              </span>
            </details>
          </li>
        );
      })}
    </ul>
  );
}
